import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Search, Mic, FileText, Feather, Loader2, Plus, Heart, Inbox, Sparkles, ArrowRight } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "inbox", label: "To develop" },
  { key: "stories", label: "Stories" },
];

function EmptyState({ onStart, filter }) {
  const msg = filter === "stories"
    ? "No stories yet. Turn a memory into a story and it will appear here."
    : filter === "inbox"
      ? "Nothing waiting — every memory has become a story. Lovely."
      : "Your captured memories will gather here as attractive cards.";
  return (
    <div className="text-center py-16 bg-white rounded-3xl border border-line">
      <h3 className="font-serif text-2xl text-charcoal">{filter === "all" ? "No memories yet" : "Nothing here yet"}</h3>
      <p className="mt-2 text-subtle">{msg}</p>
      <button data-testid="memories-start-btn" onClick={onStart} className="mt-6 inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-6 py-3 rounded-full font-semibold">
        <Plus size={18} /> Add a memory
      </button>
    </div>
  );
}

export default function Memories() {
  const navigate = useNavigate();
  const [memories, setMemories] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [developingId, setDevelopingId] = useState(null);

  const load = async (q = "") => {
    setLoading(true);
    const { data } = await api.get("/memories", { params: { search: q } });
    setMemories(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search]);

  const hasStory = (m) => !!(m.memoir_draft && m.memoir_draft.trim());
  const inboxCount = memories.filter((m) => !hasStory(m)).length;

  const shown = memories.filter((m) => {
    if (filter === "inbox") return !hasStory(m);
    if (filter === "stories") return hasStory(m);
    return true;
  });

  const develop = async (e, m) => {
    e.stopPropagation();
    setDevelopingId(m.id);
    try {
      await api.post(`/memories/${m.id}/generate-memoir`, { style: "polish" });
      toast.success("We've turned that memory into a story");
      await load(search);
    } catch {
      toast.error("Couldn't do that just now. Please try again.");
    } finally {
      setDevelopingId(null);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">My Memories</h1>
          <p className="text-subtle mt-1 text-sm">Tell us what happened — we'll help you find the story in it.</p>
        </div>
        <button data-testid="new-memory-btn" onClick={() => navigate("/answer", { state: { freeform: true } })} className="inline-flex items-center gap-2 bg-teal hover:bg-tealdark text-cream px-5 py-2.5 rounded-full font-semibold text-sm">
          <Plus size={16} /> New
        </button>
      </div>

      {/* Inbox nudge */}
      {inboxCount > 0 && filter !== "inbox" && (
        <button data-testid="inbox-nudge" onClick={() => setFilter("inbox")}
          className="w-full text-left rounded-2xl bg-coral/10 border border-coral/20 p-4 flex items-center gap-3 hover:bg-coral/15 transition-colors">
          <span className="w-10 h-10 rounded-xl bg-coral/15 text-coral flex items-center justify-center shrink-0"><Inbox size={20} /></span>
          <span className="flex-1 text-sm text-charcoal">
            <b>{inboxCount} {inboxCount === 1 ? "memory is" : "memories are"} waiting</b> to become {inboxCount === 1 ? "a story" : "stories"}.
          </span>
          <ArrowRight size={16} className="text-coral" />
        </button>
      )}

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-subtle" size={18} />
        <input
          data-testid="memories-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search your memories, people, themes…"
          className="w-full h-12 pl-11 pr-4 rounded-full border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none"
        />
      </div>

      {/* Filter chips */}
      <div className="flex gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`memories-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${filter === f.key ? "bg-teal text-cream" : "bg-white border border-line text-subtle hover:border-sage"}`}>
            {f.label}{f.key === "inbox" && inboxCount > 0 ? ` (${inboxCount})` : ""}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-teal" size={28} /></div>
      ) : shown.length === 0 ? (
        <EmptyState filter={filter} onStart={() => navigate("/answer", { state: { freeform: true } })} />
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {shown.map((m) => {
            const story = hasStory(m);
            return (
              <div
                key={m.id} data-testid={`memory-card-${m.id}`} onClick={() => navigate(`/memories/${m.id}`)}
                className="cursor-pointer text-left bg-white rounded-2xl border border-line p-6 hover:border-sage hover:shadow-md transition-all animate-fade-up"
              >
                <p className="text-xs uppercase tracking-wide text-sage font-semibold">{m.period || "Memory"}</p>
                <h3 className="font-serif text-lg text-charcoal mt-1.5 leading-snug">{m.question_text}</h3>
                <p className="mt-2 text-subtle line-clamp-3 text-sm leading-relaxed">{m.original_transcript}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {m.contributed_by && <span data-testid="memory-contributed-by" className="inline-flex items-center gap-1 text-xs text-coral bg-coral/10 px-2 py-1 rounded-full"><Heart size={12} /> Shared by {m.contributed_by}</span>}
                  {m.source === "voice" && <span className="inline-flex items-center gap-1 text-xs text-teal bg-sagelight px-2 py-1 rounded-full"><Mic size={12} /> Voice</span>}
                  {story ? (
                    <span data-testid={`memory-story-chip-${m.id}`} className="inline-flex items-center gap-1 text-xs text-teal bg-sagelight px-2 py-1 rounded-full"><Feather size={12} /> Story</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-subtle bg-cream2 px-2 py-1 rounded-full"><FileText size={12} /> Memory</span>
                  )}
                  {(m.tags || []).slice(0, 2).map((t) => <span key={t} className="text-xs text-subtle bg-cream2 px-2 py-1 rounded-full">{t}</span>)}
                </div>
                {!story && (
                  <button data-testid={`develop-memory-${m.id}`} onClick={(e) => develop(e, m)} disabled={developingId === m.id}
                    className="mt-4 inline-flex items-center gap-2 bg-coral hover:bg-coraldark text-white px-4 py-2 rounded-full font-semibold text-sm transition-colors disabled:opacity-60">
                    {developingId === m.id ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />}
                    Turn into a story
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
